import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Eye, EyeOff, Trash2, Pencil, ShieldCheck, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const REVEAL_SECONDS = 30;
const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString("es-ES", { timeZone: "America/New_York", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }) : "—";
const groupPan = (n) => (n || "").replace(/(.{4})/g, "$1 ").trim();

function CardForm({ phone, onSaved, onCancel }) {
  const [f, setF] = useState({ number: "", exp: "", cvv: "", zip: "", holder: "" });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    const [mm, yy] = f.exp.split("/").map((x) => x.trim());
    if (!mm || !yy) { toast.error("Vencimiento en formato MM/AA"); return; }
    setBusy(true);
    try {
      const { data } = await api.put(`/clients/${encodeURIComponent(phone)}/card`, {
        number: f.number, exp_month: Number(mm), exp_year: Number(yy), cvv: f.cvv, zip: f.zip, holder: f.holder,
      });
      toast.success("Tarjeta guardada (cifrada)");
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="card-form">
      <div className="space-y-1">
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Nombre en la tarjeta (opcional)</Label>
        <Input value={f.holder} onChange={upd("holder")} className="bg-input border-border uppercase" data-testid="card-holder" />
      </div>
      <div className="space-y-1">
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Número de tarjeta</Label>
        <Input value={f.number} onChange={upd("number")} inputMode="numeric" placeholder="4242 4242 4242 4242" className="bg-input border-border font-mono" data-testid="card-number" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Vence (MM/AA)</Label>
          <Input value={f.exp} onChange={upd("exp")} placeholder="08/28" className="bg-input border-border font-mono" data-testid="card-exp" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">CVV</Label>
          <Input value={f.cvv} onChange={upd("cvv")} inputMode="numeric" placeholder="123" className="bg-input border-border font-mono" data-testid="card-cvv" />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">ZIP</Label>
          <Input value={f.zip} onChange={upd("zip")} inputMode="numeric" placeholder="34112" className="bg-input border-border font-mono" data-testid="card-zip" />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel} data-testid="card-form-cancel">Cancelar</Button>
        <Button onClick={save} disabled={busy} className="bg-primary text-primary-foreground font-bold" data-testid="card-form-save">
          {busy ? "Guardando…" : "Guardar tarjeta"}
        </Button>
      </div>
    </div>
  );
}

// Pop-up "Método de pago": masked card by default, Revelar shows everything
// for 30s and is audit-logged server side. Admin can add/replace/delete.
export default function ClientCardDialog({ phone, clientName, open, onOpenChange }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [card, setCard] = useState(null);     // masked summary
  const [full, setFull] = useState(null);     // revealed data
  const [left, setLeft] = useState(0);
  const [editing, setEditing] = useState(false);
  const [log, setLog] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/clients/${encodeURIComponent(phone)}/card`);
      setCard(data);
      setEditing(!data.exists && isAdmin);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [phone, isAdmin]);

  const hide = useCallback(() => { setFull(null); setLeft(0); clearInterval(timer.current); }, []);

  useEffect(() => {
    if (open && phone) { load(); setLog(null); }
    if (!open) hide();
    return () => clearInterval(timer.current);
  }, [open, phone, load, hide]);

  const reveal = async () => {
    try {
      const { data } = await api.post(`/clients/${encodeURIComponent(phone)}/card/reveal`);
      setFull(data);
      setLeft(REVEAL_SECONDS);
      clearInterval(timer.current);
      timer.current = setInterval(() => setLeft((s) => {
        if (s <= 1) { clearInterval(timer.current); setFull(null); return 0; }
        return s - 1;
      }), 1000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar la tarjeta guardada de ${clientName || phone}?`)) return;
    try {
      await api.delete(`/clients/${encodeURIComponent(phone)}/card`);
      toast.success("Tarjeta eliminada");
      hide(); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadLog = async () => {
    try { const { data } = await api.get(`/clients/${encodeURIComponent(phone)}/card/log`); setLog(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="client-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-primary" /> Método de pago</DialogTitle>
          <DialogDescription className="text-xs">
            {clientName || "Cliente"} · <span className="font-mono">{phone}</span> · <ShieldCheck className="inline w-3 h-3 text-emerald-400" /> cifrada; cada vista queda registrada.
          </DialogDescription>
        </DialogHeader>

        {!card ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Cargando…</div>
        ) : editing ? (
          <CardForm phone={phone} onSaved={(d) => { setCard(d); setEditing(false); hide(); }} onCancel={() => setEditing(false)} />
        ) : !card.exists ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="card-empty">
            Este cliente no tiene tarjeta guardada.
            {isAdmin && <div className="mt-3"><Button onClick={() => setEditing(true)} className="bg-primary text-primary-foreground font-bold" data-testid="card-add-btn">Agregar tarjeta</Button></div>}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-zinc-900 to-zinc-950 p-4 font-mono shadow-inner" data-testid="card-visual">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-muted-foreground">
                <span>{card.brand}</span>
                {full && <span className="text-emerald-400" data-testid="card-reveal-timer">se oculta en {left}s</span>}
              </div>
              <div className="text-xl tracking-[0.18em] text-white mt-3" data-testid="card-number-display">
                {full ? groupPan(full.number) : `•••• •••• •••• ${card.last4}`}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                <div><div className="text-[9px] uppercase text-muted-foreground">Vence</div><div className="text-white" data-testid="card-exp-display">{full ? full.exp : "••/••"}</div></div>
                <div><div className="text-[9px] uppercase text-muted-foreground">CVV</div><div className="text-white" data-testid="card-cvv-display">{full ? full.cvv : "•••"}</div></div>
                <div><div className="text-[9px] uppercase text-muted-foreground">ZIP</div><div className="text-white" data-testid="card-zip-display">{full ? (full.zip || "—") : "•••••"}</div></div>
              </div>
              {card.holder && <div className="mt-3 text-xs text-muted-foreground uppercase">{card.holder}</div>}
            </div>
            <div className="text-[11px] text-muted-foreground font-mono">
              Guardada por {card.updated_by_name || "—"} · {fmtWhen(card.updated_at)}
            </div>
            <div className="flex flex-wrap gap-2">
              {full ? (
                <Button variant="outline" onClick={hide} className="border-border" data-testid="card-hide-btn"><EyeOff className="w-4 h-4 mr-1" /> Ocultar</Button>
              ) : (
                <Button onClick={reveal} className="bg-primary text-primary-foreground font-bold" data-testid="card-reveal-btn"><Eye className="w-4 h-4 mr-1" /> Revelar {REVEAL_SECONDS}s</Button>
              )}
              {isAdmin && (
                <>
                  <Button variant="outline" onClick={() => { hide(); setEditing(true); }} className="border-border" data-testid="card-replace-btn"><Pencil className="w-4 h-4 mr-1" /> Reemplazar</Button>
                  <Button variant="outline" onClick={remove} className="border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid="card-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> Eliminar</Button>
                  <Button variant="ghost" onClick={loadLog} className="ml-auto text-xs" data-testid="card-log-btn"><History className="w-4 h-4 mr-1" /> Quién la vio</Button>
                </>
              )}
            </div>
            {log && (
              <div className="rounded border border-border max-h-40 overflow-y-auto text-xs" data-testid="card-log">
                {log.length === 0 && <div className="p-3 text-muted-foreground">Nadie ha revelado esta tarjeta todavía.</div>}
                {log.map((r, i) => (
                  <div key={i} className="flex justify-between px-3 py-1.5 border-b border-border/50 last:border-0 font-mono">
                    <span className="text-white">{r.user_name} <span className="text-muted-foreground">({r.role})</span></span>
                    <span className="text-muted-foreground">{fmtWhen(r.at)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
